<template>
  <p-app theme="glittr page">
    <template slot="header">
      <p-toolbar color="base">
        <p-row>
          <p-col justify-start align-center>
            <p-button
              spacious
              tertiary
              color="white"
              flat
              @click="toggleSideDrawerVisible()"
            >
              <p-icon spacious>
                menu
              </p-icon>
            </p-button>
            <router-link to="/">
              <p-img
                v-if="$media.isNotMobile"
                class="header-logo"
                width="260"
                height="44px"
                contain
                :src="require('@src/assets/logo.svg')"
              ></p-img>
            </router-link>
          </p-col>
          <p-col justify-end>
            <p-menu v-model="profileMenuVisible" bottom left>
              <template slot="activator">
                <p-button spacious tertiary>
                  <p-icon spacious>
                    person
                  </p-icon>
                </p-button>
              </template>
              <p-card>
                <p-row column>
                  <router-link to="/_me/profile" tag="p-button">
                    {{ $t("core.page.profile.button.showProfile") }}
                  </router-link>
                  <p-button @click="doLogoff">
                    {{ $t("core.page.profile.button.logoff") }}
                    <p-icon right>
                      exit_to_app
                    </p-icon>
                  </p-button>
                </p-row>
              </p-card>
            </p-menu>
          </p-col>
        </p-row>
      </p-toolbar>
    </template>

    <p-side-drawer
      v-model="isSidedrawerVisible"
      :overlay="$media.isMobile"
      :minified.sync="isNavMinifed"
      minifiable
    >
      <template #drawer-content>
        <p-list-group>
          <p-list class="mt-3" spacious>
            <p-list-group v-if="isAdmin" icon-left="local_library">
              <template #activator>
                {{ $t("learnit.glossary.learningUnits") }}
              </template>
              <p-list-entry
                to="/ktgl/learnit/learningUnits"
                icon-left="view_module"
              >
                {{ $t("page.dashboard.label.all") }}
              </p-list-entry>
              <p-list-entry to="/ktgl/learnit/users/tags" icon-left="sell">
                {{ $t('page.admin.tags.title.tags') }}
              </p-list-entry>
            </p-list-group>
            <p-list-entry
              v-if="isInstructionUrlSet"
              icon-left="description"
              @click="openInstruction"
            >
              {{ $t("page.dashboard.label.instructions") }}
            </p-list-entry>
          </p-list>
        </p-list-group>
      </template>
      <template v-if="isAdmin" #drawer-footer-actions>
        <p-button tertiary @click="toggleSettings">
          <p-icon>settings</p-icon>
        </p-button>
      </template>
      <template #content>
        <p-side-drawer v-model="isSettingsVisible" :overlay="$media.isMobile">
          <template #drawer-header-image>
            <p-icon>settings</p-icon>
          </template>
          <template #drawer-header-title>
            {{ $t("navigation.settings.name") }}
          </template>
          <template #drawer-content>
            <p-list-group>
              <p-list class="mt-3" spacious>
                <p-list-entry to="/_admin/dashboard" icon-left="dashboard">
                  {{ $t('core.page.dashboard.page.title') }}
                </p-list-entry>
                <p-list-entry :to="`/_admin/applog?levels=error&dateFrom=${date}`" icon-left="list">
                  {{ $t('core.page.appLog.page.title') }}
                </p-list-entry>
                <p-list-entry :to="'/_admin/calllog?datefrom=${date}'" icon-left="list">
                  {{ $t('core.page.callLog.page.title') }}
                </p-list-entry>
                <p-list-entry to="/_hostadmin/users" icon-left="person">
                  {{ $t('core.page.user.page.title') }}
                </p-list-entry>
                <p-list-entry to="/_hostadmin/sequencenumbers" icon-left="pin">
                  {{ $t('core.page.sequencenumbers.page.title') }}
                </p-list-entry>
                <p-list-entry to="/widgets/widget-host-config" icon-left="settings">
                  {{ $t('core.page.widgetHostConfiguration.page.title') }}
                </p-list-entry>
              </p-list>
            </p-list-group>
          </template>
          <template v-if="$media.isMobile" #drawer-footer-actions>
            <p-button tertiary @click="isSettingsVisible = false">
              <p-icon>arrow_back</p-icon>
            </p-button>
          </template>
          <template slot="content">
            <p-container class="content">
              <p-row>
                <p-col xs12 sm12 lg12 xl2></p-col>
                <p-col xs12 sm12 lg12 xl8>
                  <p-card>
                    <slot></slot>
                  </p-card>
                </p-col>
                <p-col xs12 sm12 lg12 xl2></p-col>
              </p-row>
            </p-container>
          </template>
        </p-side-drawer>
      </template>
    </p-side-drawer>
  </p-app>
</template>

<script lang="ts">
  import Vue from 'vue';

  export default Vue.extend({
    auth: true,
    data: () => ({
      date: Vue.$date.now().format('YYYY-MM-DD'),
      profileMenuVisible: false,
      isSidedrawerVisible: true,
      isSettingsVisible: false,
      isNavMinifed: false,
      instructionUrl: undefined as string | undefined,
    }),
    computed: {
      isAdmin(): boolean {
        let isAdmin = false;
        if (this.$auth.isLoggedIn) {
          isAdmin = this.$auth.isInRole('Admin', 'HostAdmin');
        }
        return isAdmin;
      },
      isInstructionUrlSet(): boolean {
        if (this.instructionUrl === undefined || this.instructionUrl === null || this.instructionUrl === '') {
          return false;
        }
        return true;
      },
    },
    watch: {
      isNavMinifed() {
        this.$localStorage.set('isNavMinifed', this.isNavMinifed);
      },
    },
    beforeMount() {
      if (this.$media.isNotMobile) {
        this.isSidedrawerVisible = this.$localStorage.get<boolean>('isSidedrawerVisible') ?? true;
        this.isSettingsVisible = this.$localStorage.get<boolean>('isSettingsVisible') ?? false;
        this.isNavMinifed = this.$localStorage.get<boolean>('isNavMinifed') ?? false;
        this.$nextTick(() => {
          this.isNavMinifed = this.$localStorage.get<boolean>('isNavMinifed') ?? false;
        });
      }
      this.instructionUrl = this.$config.values['learnit-instructions-url'];
    },
    methods: {
      async doLogoff() {
        try {
          await this.$auth.logout();
          this.$router.push('/_exterior/login');
        } catch (error) {
          this.$log.error('Unable to log out an error occurred');
          this.$log.error(error);
        }
      },
      toggleSideDrawerVisible(isVisible?: boolean) {
        const newVisibleState = isVisible ?? !this.isSidedrawerVisible;
        this.isSidedrawerVisible = newVisibleState;
        this.$localStorage.set('isSidedrawerVisible', this.isSidedrawerVisible);
      },
      toggleSettings() {
        this.isSettingsVisible = !this.isSettingsVisible;
        this.$localStorage.set('isSettingsVisible', this.isSettingsVisible);
        if (this.$media.isMobile) {
          this.isSidedrawerVisible = false;
        } else {
          this.isNavMinifed = this.isSettingsVisible;
        }
      },
      openInstruction() {
        window.open(this.instructionUrl);
      },
    },
  });
</script>
